import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { PaHttpClient } from 'src/app/clients/http-client';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ToastrService } from 'src/app/shared/services/toastr.service';
import { AssignSpPopupComponent, AssignType } from 'src/app/views/requests/detail/assign-sp-popup/assign-sp-popup.component';
import { ActivityRefreshService } from 'src/app/shared/services/activity-refresh.service';
import { timeout } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type FrontdeskStep = 'select_type' | 'select_subtype' | 'select_datetime' | 'review_details';

interface RequestCategory {
  value: string;
  label: string;
  description: string;
  image: string;
}

interface InstructionSubType {
  value: string;
  label: string;
  description: string;
}

const REQUEST_CATEGORIES: RequestCategory[] = [
  {
    value: 'complaints_concerns',
    label: 'Complaints & Concerns',
    description: 'Contact the front desk to address any issues, including noise complaints or security concerns.',
    image: 'assets/icons/temporarypass.svg',
  },
  {
    value: 'lost_found',
    label: 'Lost & Found',
    description: 'For lost and found items, please send us a request to report or reclaim lost belongings.',
    image: 'assets/icons/fill-icons/items.svg',
  },
  {
    value: 'instructions',
    label: 'Instructions',
    description: 'Request assistance with lockouts, reminders, quick notes',
    image: 'assets/icons/onetimeentry.svg',
  },
];

const INSTRUCTION_SUBTYPES: InstructionSubType[] = [
  {
    value: 'wake_up_call',
    label: 'Wake Up Call',
    description: 'Request front desk to call at a specified time to wake up guest.',
  },
  {
    value: 'reminders',
    label: 'Reminders',
    description: 'Request a reminder call for an important appointment.',
  },
  {
    value: 'event_notes',
    label: 'Event Notes',
    description: 'Notes for front desk to assist resident for an important tasks or event',
  },
];

function getTodayDate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

@Component({
  selector: 'pa-frontdesk-requests-create',
  imports: [CommonModule, DialogModule,AssignSpPopupComponent],
  templateUrl: './frontdesk-requests-create.component.html',
  styleUrls: ['./frontdesk-requests-create.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PaHttpClient],
  host: { '(document:click)': 'onDocClick($event)' },
})
export class FrontdeskRequestsCreateComponent implements OnInit {
  readonly mode = input<'create' | 'edit'>('create');
  readonly editData = input<any | null>(null);

  readonly close = output<void>();
  readonly requestComplete = output<unknown>();
private readonly destroyRef = inject(DestroyRef);
  private readonly http = inject(PaHttpClient);
  private readonly auth = inject(AuthService);
  private readonly toastr = inject(ToastrService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly activityRefresh = inject(ActivityRefreshService);

  readonly currentUser = this.auth.getUser();
  readonly categories = REQUEST_CATEGORIES;
  readonly instructionSubtypes = INSTRUCTION_SUBTYPES;

  // ── Navigation ───────────────────────────────────────────────
  readonly currentStep = signal<FrontdeskStep>('select_type');
  readonly showComingSoon = signal(false);

  // ── Step 1 ───────────────────────────────────────────────────
  readonly selectedCategory = signal<RequestCategory | null>(null);

  // ── Step 2 ───────────────────────────────────────────────────
  readonly selectedSubType = signal<InstructionSubType | null>(null);

  // ── Step 3: Date & Time ──────────────────────────────────────
  readonly selectedDate = signal<string>(getTodayDate());
  readonly selectedHour = signal<string>('09');
  readonly selectedMinute = signal<string>('00');
  readonly showHourDropdown = signal(false);
  readonly showMinuteDropdown = signal(false);

  readonly hoursList: { value: string; title: string }[] = Array.from({ length: 24 }, (_, x) => {
    const v = x < 10 ? '0' + x : String(x);
    return { value: v, title: `${v} (${x % 12 || 12} ${x < 12 ? 'AM' : 'PM'})` };
  });
  readonly minutesList: string[] = Array.from({ length: 60 }, (_, x) => String(x).padStart(2, '0'));

  // ── Step 4: Description form ─────────────────────────────────
  readonly title = signal('');
  readonly description = signal('');
  readonly fullName = signal('');
  readonly email = signal('');
  readonly workNumber = signal('');
  readonly address = signal('');

  // ── Use AI (title/description generation) ─────────────────────
  readonly showSuggestion = signal(false);
  readonly aiTargetField = signal<'title' | 'desc' | null>(null);
  readonly isAiGenerating = signal(false);
  readonly suggestedAiContent = signal('');
  private aiRequestSeq = 0;

  // ── Step 5: Review ───────────────────────────────────────────
  readonly selectedManagement = signal<any | null>(null);
  readonly selectedFrontDesk = signal<any | null>(null);
  readonly noteText = signal('');

  // ── Assign popup ─────────────────────────────────────────────
  readonly showAssignPopup = signal(false);
  readonly assignPopupType = signal<AssignType>('management');
  readonly assignPopupTitle = signal('');

  // ── Submit ───────────────────────────────────────────────────
  readonly isSubmitting = signal(false);
  readonly showSuccessPopup = signal(false);

  ngOnInit(): void {
    if (this.mode() === 'edit') {
      this.populateEditForm();
      return;
    }
    const u = this.currentUser;
    this.fullName.set(`${u?.first_name ?? ''} ${u?.last_name ?? ''}`.trim());
    this.email.set(u?.email ?? '');
    this.workNumber.set(u?.mobile ?? '');
    const addrs: any[] = (u as any)?.addresses ?? [];
    if (addrs.length) {
      const a = addrs[0];
      this.address.set(
        [a.address, a.apartment ? `Apt ${a.apartment}` : '', a.city, a.state?.short_name ?? '', a.zipcode]
          .filter(Boolean).join(', ')
      );
    }
  }

  private populateEditForm(): void {
    const d = this.editData();
    if (!d) return;

    this.selectedCategory.set(this.categories.find(c => c.value === d.request_type) ?? null);

    const subTypeValue = d.sub_type || d.service_sub_type;
    const matchedSubType = this.instructionSubtypes.find(s => s.value === subTypeValue);
   
    const fallbackLabel = d.service_sub_type_title || d.requests_type_title;
    this.selectedSubType.set(
      matchedSubType ?? (fallbackLabel ? { value: subTypeValue ?? '', label: fallbackLabel, description: '' } : null)
    );

    if (d.pickup_date) this.selectedDate.set(d.pickup_date);
    if (d.pickup_time) {
      const [h, m] = String(d.pickup_time).split(':');
      if (h) this.selectedHour.set(h.padStart(2, '0'));
      if (m) this.selectedMinute.set(m.padStart(2, '0'));
    }

    this.title.set(d.title ?? '');
    this.description.set(d.description ?? '');

    // The request itself doesn't carry its own contact fields — they live on the
    // tenant/resident who created it, so pull Full Name / Email / Work Number /
    // Address from createdby_detail rather than (mostly absent) top-level fields.
    const creator = d.createdby_detail ?? {};
    this.fullName.set(d.full_name || `${creator.first_name ?? ''} ${creator.last_name ?? ''}`.trim());
    this.email.set(d.email || creator.email || '');
    this.workNumber.set(d.work_number || creator.mobile || '');

    const creatorAddr = creator.addresses?.[0];
    this.address.set(
      d.address ||
      (creatorAddr
        ? [
            creatorAddr.address,
            creatorAddr.apartment ? `Apt ${creatorAddr.apartment}` : '',
            creatorAddr.city,
            creator.state?.state_name ?? '',
            creatorAddr.zipcode,
          ].filter(Boolean).join(', ')
        : '')
    );

    this.noteText.set(d.notes ?? '');
    this.selectedManagement.set(d.management_detail ?? null);
    this.selectedFrontDesk.set(d.front_desk_details ?? null);

    this.currentStep.set('review_details');
  }

  onDocClick(e: MouseEvent): void {
    if (this.showHourDropdown() || this.showMinuteDropdown()) {
      if (!(e.target as HTMLElement).closest('.time-dropdown-wrap')) {
        this.showHourDropdown.set(false);
        this.showMinuteDropdown.set(false);
      }
    }
  }

  // ── Navigation ───────────────────────────────────────────────
  onBack(): void {
    switch (this.currentStep()) {
      case 'select_type':      this.close.emit(); break;
      case 'select_subtype':   this.currentStep.set('select_type'); break;
      case 'select_datetime':  this.currentStep.set('select_subtype'); break;
      case 'review_details':   this.currentStep.set('select_datetime'); break;
    }
  }

  onClose(): void { this.close.emit(); }

  // ── Step 1 ───────────────────────────────────────────────────
  selectCategory(cat: RequestCategory): void {
    this.selectedCategory.set(cat);
    if (cat.value === 'instructions') {
      this.currentStep.set('select_subtype');
    } else {
      this.showComingSoon.set(true);
    }
  }

  dismissComingSoon(): void { this.showComingSoon.set(false); }

  // ── Step 2 ───────────────────────────────────────────────────
  selectSubType(sub: InstructionSubType): void {
    this.selectedSubType.set(sub);
    this.currentStep.set('select_datetime');
  }

  // ── Step 3 ───────────────────────────────────────────────────
  onDateTimeNext(): void { this.currentStep.set('review_details'); }

  getFormattedDate(): string {
    const [, month, day] = this.selectedDate().split('-').map(Number);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[month - 1]} ${String(day).padStart(2, '0')}`;
  }

  getDayOfWeek(): string {
    const [year, month, day] = this.selectedDate().split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', { weekday: 'long' });
  }

  getHourLabel(): string {
    const h = parseInt(this.selectedHour(), 10);
    return `${h % 12 || 12} ${h < 12 ? 'AM' : 'PM'}`;
  }

  openDatePicker(input: HTMLInputElement): void {
    try { input.showPicker(); } catch { input.click(); }
  }

  onDateChange(e: Event): void {
    this.selectedDate.set((e.target as HTMLInputElement).value);
  }

  toggleHourDropdown(e: MouseEvent): void {
    e.stopPropagation();
    this.showHourDropdown.set(!this.showHourDropdown());
    this.showMinuteDropdown.set(false);
  }

  toggleMinuteDropdown(e: MouseEvent): void {
    e.stopPropagation();
    this.showMinuteDropdown.set(!this.showMinuteDropdown());
    this.showHourDropdown.set(false);
  }

  selectHour(i: number): void {
    this.selectedHour.set(this.hoursList[i].value);
    this.showHourDropdown.set(false);
  }

  selectMinute(i: number): void {
    this.selectedMinute.set(this.minutesList[i]);
    this.showMinuteDropdown.set(false);
  }

  getFormattedDateTime(): string {
    const [year, month, day] = this.selectedDate().split('-').map(Number);
    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const h = parseInt(this.selectedHour(), 10);
    return `${months[month - 1]} ${String(day).padStart(2, '0')}, ${year}, ${h % 12 || 12}:${this.selectedMinute()} ${h < 12 ? 'AM' : 'PM'}`;
  }

  getReviewTimeLabel(): string {
    const h = parseInt(this.selectedHour(), 10);
    return `${h % 12 || 12}:${this.selectedMinute()}${h < 12 ? 'AM' : 'PM'}`;
  }

  getUserFullName(): string {
    const u = this.currentUser;
    return `${u?.first_name ?? ''} ${u?.last_name ?? ''}`.trim();
  }

  getPickupDateTimeLabel(): string {
    const [year, month, day] = this.selectedDate().split('-').map(Number);
    const d = new Date(year, month - 1, day);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    const h = parseInt(this.selectedHour(), 10);
    return `${months[d.getMonth()]} ${d.getDate()}, ${days[d.getDay()]} ${h % 12 || 12}:${this.selectedMinute()}${h < 12 ? 'AM' : 'PM'}`;
  }

  getUserAvatar(): string {
    return this.currentUser?.profile_picture || 'assets/images/dummy-avatar.jpg';
  }

  // ── Assign popup ─────────────────────────────────────────────
  openAssign(type: AssignType, title: string): void {
    this.assignPopupType.set(type);
    this.assignPopupTitle.set(title);
    this.showAssignPopup.set(true);
  }

  onAssignSelected(item: any | any[] | null): void {
    this.showAssignPopup.set(false);
    if (!item || Array.isArray(item)) return;
    if (this.assignPopupType() === 'management') {
      this.selectedManagement.set(item);
    } else if (this.assignPopupType() === 'front_desk') {
      this.selectedFrontDesk.set(item);
    }
  }

  getAssigneeName(item: any): string {
    return item?.service_provider_name || item?.management_name ||
      `${item?.first_name ?? ''} ${item?.last_name ?? ''}`.trim() || '—';
  }

  getAssigneeAvatar(item: { profile_picture?: string; documents?: { url?: string }[] } | null | undefined): string | null {
    const url = item?.profile_picture || item?.documents?.[0]?.url;
    return typeof url === 'string' && url.trim() ? url : null;
  }

  isPictureBlocked(status: string | null | undefined): boolean {
    return status === 'flagged' || status === 'blocked';
  }

  // ── Use AI (title/description generation) ─────────────────────
  openSuggestion(field: 'title' | 'desc' = 'desc'): void {
    const requestSeq = ++this.aiRequestSeq;
    this.aiTargetField.set(field);
    this.showSuggestion.set(true);
    this.suggestedAiContent.set('');
    this.isAiGenerating.set(true);

    const contentToUse = field === 'desc'
      ? (this.description() || this.title())
      : this.title();
    if (!contentToUse) {
      this.isAiGenerating.set(false);
      return;
    }

    const body = { description: contentToUse };
    this.http.post('reminderRequests/description', body)
      .pipe(timeout(15000), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          if (requestSeq !== this.aiRequestSeq) return;
          if (res.success) {
            this.suggestedAiContent.set(res.data.descriptions1);
          }
          this.isAiGenerating.set(false);
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          if (requestSeq !== this.aiRequestSeq) return;
          console.error('Error generating AI content:', err);
          this.isAiGenerating.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  closeSuggestion(): void {
    this.aiRequestSeq++;
    this.showSuggestion.set(false);
    this.suggestedAiContent.set('');
    this.aiTargetField.set(null);
    this.isAiGenerating.set(false);
  }

  useSuggestion(): void {
    const content = this.suggestedAiContent();
    if (!content) return;
    if (this.aiTargetField() === 'title') {
      this.title.set(content);
    } else {
      this.description.set(content);
    }
    this.closeSuggestion();
  }

  // ── Submit ───────────────────────────────────────────────────
  onSubmit(): void {
    if (this.isSubmitting()) return;
    if (!this.title().trim() || !this.description().trim()) {
      this.toastr.showError('Please fill in the title and description.');
      return;
    }
    this.isSubmitting.set(true);

    const payload: Record<string, unknown> = {
      service_type: 'frontdesk',
      request_type: this.selectedCategory()!.value,
      // Only send a subtype we could actually resolve. Legacy records may carry just a
      // display title (service_sub_type_title / requests_type_title) with no canonical
      // value, in which case populateEditForm falls back to value: ''. Sending that
      // empty string on edit would overwrite the request's persisted subtype — omitting
      // the key instead leaves the stored value untouched.
      ...(this.selectedSubType()?.value
        ? { service_sub_type: this.selectedSubType()!.value }
        : {}),
      title: this.title().trim(),
      description: this.description().trim(),
      pickup_date: this.selectedDate(),
      pickup_time: `${this.selectedHour()}:${this.selectedMinute()}:00`,
      full_name: this.fullName().trim(),
      email: this.email().trim(),
      work_number: this.workNumber().trim(),
      address: this.address().trim(),
      ...(this.noteText().trim() && { notes: this.noteText().trim() }),
      ...(this.selectedManagement() && { management_id: String(this.selectedManagement()!.user_id) }),
      ...(this.selectedFrontDesk() && { front_desk_id: String(this.selectedFrontDesk()!.user_id) }),
    };

    const isEdit = this.mode() === 'edit';
    const request$ = isEdit
      ? this.http.put(`request/update/${this.editData()?.request_id}`, payload)
      : this.http.post('request/add', payload);

    request$.pipe(timeout(15000), takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.isSubmitting.set(false);
        this.activityRefresh.triggerRefresh();
        this.showSuccessPopup.set(true);
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.isSubmitting.set(false);
        this.toastr.showError(err?.error?.message || `Failed to ${isEdit ? 'update' : 'submit'} request. Please try again.`);
        this.cdr.markForCheck();
      },
    });
  }

  onDismiss(): void { this.close.emit(); }

  onSuccessOk(): void {
    this.showSuccessPopup.set(false);
    this.requestComplete.emit({ success: true });
    this.close.emit();
  }
}
